// Shared vocabulary for the verb+target ability model.
//
// When an Ability has a `target` field set, its `effect` is a Verb from the
// VERBS list below and the engine dispatches via Targeting + Verbs. When
// `target` is absent, `effect` is an atomic slug handled by the legacy
// EFFECT_HANDLERS map in src/engine/AbilityProcessor.ts (used for player-
// surface effects, compound/conditional effects, and passives).

export type Verb =
  | 'damage'
  | 'boost'
  | 'armor'
  | 'exhaust'
  | 'keep-exhausted'
  | 'ready'
  | 'return'
  | 'destroy'
  | 'slay'
  | 'silence';

export type Target =
  | 'self'
  | 'weakest-ally'
  | 'strongest-ally'
  | 'all-allies'
  | 'all-other-allies'
  | 'weakest-enemy'
  | 'strongest-enemy'
  | 'all-enemies'
  | 'all-characters'
  | 'strongest-character'
  | 'weakest-character'
  | 'self-and-weakest-enemy'
  | 'self-and-strongest-enemy';

export const VERB_NAMES: readonly Verb[] = [
  'damage', 'boost', 'armor', 'exhaust', 'keep-exhausted', 'ready', 'return', 'destroy', 'slay', 'silence',
];

export const TARGET_NAMES: readonly Target[] = [
  'self',
  'weakest-ally', 'strongest-ally',
  'all-allies', 'all-other-allies',
  'weakest-enemy', 'strongest-enemy', 'all-enemies',
  'all-characters',
  'strongest-character', 'weakest-character',
  'self-and-weakest-enemy', 'self-and-strongest-enemy',
];

export function isVerb(x: string): x is Verb {
  return (VERB_NAMES as readonly string[]).includes(x);
}

export function isTarget(x: string): x is Target {
  return (TARGET_NAMES as readonly string[]).includes(x);
}

// Whether the target returns a group of instance IDs. Used by rule text
// generation to pick singular vs plural phrasing for e.g. `slay`.
// `strongest-character` / `weakest-character` are singular — they pick
// one character (with tie-favor toward the active player), not a
// group.
export function isGroupTarget(target: Target): boolean {
  return target === 'all-allies'
    || target === 'all-other-allies'
    || target === 'all-enemies'
    || target === 'all-characters'
    || target === 'self-and-weakest-enemy'
    || target === 'self-and-strongest-enemy';
}

// Verbs that *help* their target (boost / armor / ready). Negative
// verbs (damage / exhaust / return / destroy / slay) hurt their
// target. Used by the cross-side `strongest-character` and
// `weakest-character` targets to bias tie-breaks toward whichever
// side benefits the active player — they win ties either way, just
// on different sides depending on the verb's sign. Mirrors
// `VERB_SIGN_ALLY` in `src/shared/balance.ts`; keep these in sync.
const POSITIVE_VERBS: ReadonlySet<string> = new Set<string>(['boost', 'armor', 'ready']);

export function isPositiveVerb(verb: string): boolean {
  return POSITIVE_VERBS.has(verb);
}
